import { ForecastSlice, useCurrentMarketLocation, useForecastData, useSnackbar } from '@e2m-customer-portal/hooks';
import { convertMeasure, InputFieldType, parseNumber } from '@e2m-customer-portal/shared';
import { eachDayOfInterval, getTime, Interval, isAfter, isBefore } from 'date-fns';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
  EditableMarket,
  ForecastProperty,
  ForecastSliceInput,
  ProductService,
  Resolution,
  SpotPriceForecast,
} from '../../../../generated/graphql';

import { AbortConfirmationDialog, ErrorComponent } from '@e2m-customer-portal/ui';
import { useTranslation } from 'react-i18next';
import { getEarliestMarketingDeadline, getMarketingDeadline } from '../../../../utils';
import { DeviceWithInstalledPower } from '../../control-energy-forecast/ControlEnergyForecast';
import { SaveBar } from '../save-bar/SaveBar';
import { ProductTable } from '../ShortTermForecast';
import { DeviceTable } from './forecast-table/DeviceTable';
import { DeviceTableMobile } from './forecast-table/forecast-table-mobile/DeviceTableMobile';
import { useForecastDataMutation } from './forecast-table/queries';
import { SpotPriceResult } from './spot-price';
import { correctedSlicesAccordingToDST, rowFromDate, transformToControlEnergySlices } from './utils';

export interface SelectedCell {
  row: number;
  column: number;
  value: string | undefined;
}

export interface ForecastListProps {
  /**
   * All potential devices to display in the table. Their forecast data will be loaded.
   */
  devices: readonly DeviceWithInstalledPower[];

  /**
   * The selected devices to display in the table. They will be sorted alphabetically
   * by name. If not given, all devices will be displayed.
   */
  shownDevices?: readonly DeviceWithInstalledPower[];

  /**
   * The start and end date of the displayed values.
   */
  interval: Interval;

  /**
   * Spot price forecast.
   */
  spotPriceForecast?: SpotPriceForecast;

  /**
   * Flag to indicate whether spot prices should be shown
   */
  showSpotPrices?: boolean;

  /**
   * Retrieves spot price for given date
   */
  spotPrice: (sliceDate: Date) => SpotPriceResult | undefined;

  /**
   * Flag to indicate whether the editing status is active
   */
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;

  /**
   * Whether to show the production or the control energy table
   */
  productTable: ProductTable;

  /**
   * Sorted product services
   */
  sortedProductServices?: ProductService[];
  /**
   * Flag for hide/show elements on smartphones or tablets
   */
  isMobile?: boolean;

  /**
   * Whether to show the 15 minutes resolution table
   */
  currentForecastProperty?: ForecastProperty;

  /**
   * Whether to force read-only state
   */
  forceReadOnly?: boolean;
}

export function ForecastList({
  devices,
  shownDevices,
  interval,
  spotPriceForecast,
  showSpotPrices,
  spotPrice,
  setIsEditing,
  productTable,
  isMobile,
  currentForecastProperty,
  forceReadOnly,
  sortedProductServices,
}: Readonly<ForecastListProps>): ReactElement<ForecastListProps> {
  const [t] = useTranslation();
  const resolution = currentForecastProperty?.resolution ?? Resolution.ONE_HOUR;

  const marketLocation = useCurrentMarketLocation();
  const [forecasts, error] = useForecastData(
    devices.map((device) => device.id),
    interval,
    resolution,
  );

  const daysInForecast = eachDayOfInterval(interval).filter((date) => getTime(date) !== getTime(interval.end));

  const deadlineDates = useMemo(() => new Map<string, Date>(), []);
  const slices = useMemo(() => new Map<string, readonly ForecastSlice[]>(), []);
  const controlEnergySlices = new Map<string, readonly ForecastSlice[]>();
  const defaultOverviewMap = new Map<string, Array<Array<HTMLInputElement | null>>>();

  for (const device of devices) {
    const forecast = forecasts?.find((f) => f.deviceId === device.id);
    if (!forecast) {
      throw new TypeError(`Data Error: Forecast for device with ID "${device.id}" not found`);
    }

    const deadlineDate = device.deadlines.length
      ? getMarketingDeadline(getEarliestMarketingDeadline(device.deadlines))
      : undefined;
    if (deadlineDate) {
      deadlineDates.set(device.id, deadlineDate);
    }

    slices.set(
      device.id,
      [...forecast.slices].sort(
        (a, b) =>
          rowFromDate(a.date, resolution === Resolution.FIFTEEN_MINUTES) -
          rowFromDate(b.date, resolution === Resolution.FIFTEEN_MINUTES),
      ),
    );

    controlEnergySlices.set(
      device.id,
      transformToControlEnergySlices(forecast.slices, device.products).sort(
        (a, b) =>
          rowFromDate(a.date, resolution === Resolution.FIFTEEN_MINUTES) -
          rowFromDate(b.date, resolution === Resolution.FIFTEEN_MINUTES),
      ),
    );

    const timeResolution = resolution === Resolution.FIFTEEN_MINUTES ? 96 : 24;

    const overviewMap = new Array<Array<HTMLInputElement | null>>();
    for (let row = 0; row < timeResolution; row++) {
      overviewMap.push(new Array<HTMLInputElement | null>());
    }

    defaultOverviewMap.set(device.id, overviewMap);
  }

  /**
   * Overview map is a map with matrices of refs to each input element per device table,
   * to access the element's value and set the focus using keyboard events, etc.
   */
  const [overviewMap, setOverviewMap] = useState(defaultOverviewMap);
  const [dstSliceValues, setDSTSliceValues] = useState(
    new Map<string, Array<Array<{ slice: ForecastSlice; value: string }>>>(),
  );

  const [resetTrigger, setResetTrigger] = useState(false);
  const [editedSlices, setEditedSlices] = useState(new Set<string>());
  const [editedDevices, setEditedDevices] = useState<string[]>([]);
  const [erroneousSlices, setErroneousSlices] = useState(new Set<string>());

  const [update, { loading }] = useForecastDataMutation();
  const { showError, showSuccess } = useSnackbar();

  const saveChanges = useCallback(async () => {
    if (erroneousSlices.size) return;

    const changesToBeSaved = new Map<string, Array<ForecastSlice>>(
      [...slices].map(([deviceId, deviceSlices]) => [
        deviceId,
        deviceSlices.filter((slice) => editedSlices.has(slice.id)),
      ]),
    );

    for (const [deviceId, deviceSlices] of changesToBeSaved) {
      /**
       * Because during DST changes there might be a slice missing or more than usual,
       * we need to fix the index calculation by bodging the slice array
       */
      const allDeviceSlices = slices.get(deviceId);
      if (!allDeviceSlices) throw new TypeError(`Data Error: No slices for device with ID "${deviceId}" found`);
      const correctedSlices = correctedSlicesAccordingToDST(
        allDeviceSlices,
        daysInForecast.length,
        resolution === Resolution.FIFTEEN_MINUTES,
      );

      const deviceSliceInputs = new Array<ForecastSliceInput>();

      const deviceMap = overviewMap.get(deviceId);
      if (!deviceMap) throw new TypeError(`Data Error: Table overview for device with ID "${deviceId}" not found`);

      const deviceDSTValues = dstSliceValues.get(deviceId)?.flat() ?? [];
      const dstSliceIDs = deviceDSTValues.map((dstValue) => dstValue.slice.id);

      for (const slice of deviceSlices) {
        // In case of special DST handling, we skip the normal table handling completely
        if (dstSliceIDs.includes(slice.id)) continue;

        const sliceIndex = correctedSlices.findIndex((otherSlice) => slice.id === otherSlice.id);
        if (sliceIndex === -1)
          throw new TypeError(`Data Error: Slice with ID "${slice.id}" not found in device with ID "${deviceId}"`);

        const row = rowFromDate(slice.date, resolution === Resolution.FIFTEEN_MINUTES);
        const column = sliceIndex % daysInForecast.length;
        const value = deviceMap[row][column]?.value ?? '';

        const fields = [
          {
            type: InputFieldType.PRODUCTION,
            watt: convertMeasure({ value: parseNumber(value), unit: 'kW' }, 'W').value,
          },
        ];

        deviceSliceInputs.push({
          date: slice.date,
          fields,
          hasDowntime: false,
        });
      }

      // Commit any changes in DST slices, if there are any
      for (const dstValue of deviceDSTValues) {
        if (editedSlices.has(dstValue.slice.id))
          deviceSliceInputs.push({
            date: dstValue.slice.date,
            fields: [
              {
                type: InputFieldType.PRODUCTION,
                watt: convertMeasure({ value: parseNumber(dstValue.value), unit: 'kW' }, 'W').value,
              },
            ],
            hasDowntime: false,
          });
      }

      // Now the actual save process begins
      if (deviceSliceInputs.length) {
        const dayAheadDeadline = deadlineDates.get(deviceId);
        const hasIntradayChanges =
          dayAheadDeadline && deviceSliceInputs.some((input) => isBefore(input.date, dayAheadDeadline));
        const hasDayAheadChanges =
          dayAheadDeadline && deviceSliceInputs.some((input) => isAfter(input.date, dayAheadDeadline));

        try {
          await update(deviceId, interval, deviceSliceInputs, resolution);
          if (!currentForecastProperty?.editableMarkets.includes(EditableMarket.INTRADAY)) {
            showSuccess(t('FORECAST_WIZARD_SUCCESS'));
          } else if (hasIntradayChanges && hasDayAheadChanges) {
            showSuccess(t('FORECAST_WIZARD_SUCCESS_DAY_AHEAD_AND_INTRADAY'));
          } else if (hasIntradayChanges) {
            showSuccess(t('FORECAST_WIZARD_SUCCESS_INTRADAY'));
          } else {
            showSuccess(t('FORECAST_WIZARD_SUCCESS_DAY_AHEAD'));
          }
          setEditedSlices(new Set<string>());
        } catch (error) {
          showError(error as Error);
          console.error(error);
        }
      }
    }
  }, [
    daysInForecast.length,
    deadlineDates,
    editedSlices,
    erroneousSlices.size,
    interval,
    currentForecastProperty,
    overviewMap,
    resolution,
    showError,
    showSuccess,
    slices,
    t,
    update,
  ]);

  useEffect(() => {
    if (!!editedSlices.size || !!erroneousSlices.size) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedSlices.size, erroneousSlices.size]);

  useEffect(() => {
    slices.forEach((deviceSlices, deviceId) => {
      if (!editedDevices.includes(deviceId) && deviceSlices.some((slice) => editedSlices.has(slice.id))) {
        setEditedDevices([...editedDevices, deviceId]);
      } else if (editedDevices.includes(deviceId) && !deviceSlices.some((slice) => editedSlices.has(slice.id))) {
        setEditedDevices(editedDevices.filter((id) => id !== deviceId));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedSlices.size]);

  const [selectedMobileTable, setSelectedMobileTable] = useState<string>();

  const resetTables = () => {
    setResetTrigger(!resetTrigger);
    setEditedSlices(new Set());
    setErroneousSlices(new Set());
  };

  if (error) {
    return <ErrorComponent error={error} />;
  }

  return isMobile ? (
    <>
      {devices
        .filter((device) => !selectedMobileTable || (selectedMobileTable && device.id === selectedMobileTable))
        .map((device) => (
          <DeviceTableMobile
            key={`device-table-${device.id}`}
            marketLocation={marketLocation}
            device={device}
            slices={slices.get(device.id) ?? []}
            productTable={productTable}
            spotPriceForecast={spotPriceForecast}
            spotPrice={spotPrice}
            interval={interval}
            currentForecastProperty={currentForecastProperty}
            setSelectedMobileTable={setSelectedMobileTable}
            controlEnergySlices={controlEnergySlices.get(device.id) ?? []}
          />
        ))}
    </>
  ) : (
    // after the last device we add 85px padding to make sure the SaveBar has enough space and does not cover the device table
    <div className="last-of-type:pb-[85px] bg-blue-grey-100/10">
      {(shownDevices ?? devices).map((device, index) => (
        <DeviceTable
          key={`device-table-${device.id}`}
          deviceIndex={index}
          marketLocation={marketLocation}
          device={device}
          interval={interval}
          deadlineDate={deadlineDates.get(device.id)}
          slices={slices.get(device.id) ?? []}
          controlEnergySlices={controlEnergySlices.get(device.id) ?? []}
          daysInForecast={daysInForecast}
          spotPriceForecast={spotPriceForecast}
          showSpotPrices={showSpotPrices}
          spotPrice={spotPrice}
          readOnly={!marketLocation.permissions.editForecast || forceReadOnly}
          resetTrigger={resetTrigger}
          isBeingEdited={!!editedSlices.size || !!erroneousSlices.size}
          editedSlices={editedSlices}
          editedDevices={editedDevices}
          overviewMap={overviewMap}
          dstSliceValues={dstSliceValues}
          setEditedSlices={setEditedSlices}
          setErroneousSlices={setErroneousSlices}
          setOverviewMap={setOverviewMap}
          setDSTSliceValues={setDSTSliceValues}
          saveChanges={saveChanges}
          productTable={productTable}
          currentForecastProperty={currentForecastProperty}
          sortedProductServices={sortedProductServices}
        />
      ))}
      <SaveBar
        open={!!editedSlices.size || !!erroneousSlices.size}
        saving={loading}
        isValid={!erroneousSlices.size}
        onCancel={resetTables}
        onSave={saveChanges}
      />
      <AbortConfirmationDialog when={!!editedSlices.size || !!erroneousSlices.size} />
    </div>
  );
}
